import { View, RadioGroup, Button, Card } from "@/components/ui";
import { useBookingState } from "@/store/booking/useBookingState";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { ListHeading } from "@/components/shared/list-header";
import { Cube } from "@/components/shared/icons";
import { useEffect, useMemo } from "react";
import {
  getHomeBeautyPackageFromCode,
  homeBeautyServiceTypeOptions,
} from "@/constants/booking";
import { formatPrice } from "@/lib/helpers/number";
import { showToast } from "@/components/ui/toast/show-toast";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import { getServiceUnit } from "@/lib/booking-lib";
import { Spin } from "@/components/shared/spin";
import { getPlatform } from "@/lib/utils";
import { Typography } from "@/components/shared/typography";
import { ServiceRadioCard } from "../../service-radio-card";
import { PackageServiceTypeEnum } from "@/__generated__/graphql";

const EXCLUDED_CODE = "HBEGMM";

export const HomeBeautyServiceModal = () => {
  const { forward } = useBookingRoute();
  const {
    data: {
      chooseServicePackageModal,
      packageDetailsFilter,
      packageDetails,
      selectedPackageIds,
      choosePackageLoading,
    },
    setBookingState,
  } = useBookingState();
  const platform = getPlatform();

  const service = useMemo(() => {
    return packageDetailsFilter.serviceTypes?.[0] || PackageServiceTypeEnum.Combo;
  }, [packageDetailsFilter.serviceTypes?.[0]]);

  const options = useMemo(() => {
    return packageDetails
      .filter(
        (option) =>
          option.serviceType === service && option.code !== EXCLUDED_CODE,
      )
      .map((pkg) => {
        const homeBeautyPkg = getHomeBeautyPackageFromCode(pkg.code);
        return {
          title: homeBeautyPkg.title,
          description: homeBeautyPkg.description,
          imgUrl: homeBeautyPkg.imgUrl,
          value: pkg.id,
          subTitle: `Starting ${formatPrice(pkg.serviceBillingValue)}/${getServiceUnit("home-beauty")}`,
          thirdTitle: `${pkg.duration * 60} mins`,
        };
      });
  }, [service, packageDetails]);

  useEffect(() => {
    if (chooseServicePackageModal) {
      setBookingState({
        packageDetailsFilter: {
          ...packageDetailsFilter,
          serviceTypes: [homeBeautyServiceTypeOptions[0].value],
        },
      });
    }
  }, [chooseServicePackageModal]);

  useEffect(() => {
    if (chooseServicePackageModal) {
      if (options.length) {
        setBookingState({
          selectedPackageIds: [options[0].value],
        });
      }
    }
  }, [chooseServicePackageModal, options]);

  const onSelect = (value: string) => {
    setBookingState({
      selectedPackageIds: [value],
    });
  };

  const onContinue = () => {
    if (!selectedPackageIds.length) {
      return showToast({
        title: "Please choose your package!",
        type: "info",
      });
    }
    setBookingState({
      chooseServicePackageModal: false,
      homeBeautyFreeGelServiceIds: [],
    });
    forward();
  };

  const getPlatformTitle = (title: string) => {
    if (platform === "native" && title === "Manicure Pedicure")
      return "ManiPedi";
    return title;
  };

  return (
    <BottomDrawerModal
      open={chooseServicePackageModal}
      onOpenChange={() => {
        setBookingState({
          chooseServicePackageModal: false,
        });
      }}
    >
      <View className="flex flex-col gap-4">
        <ListHeading title="Select Service" icon={Cube} />
        <Typography variant="body-sm" color="foreground-intermediate">
          Choose the service category that suits your needs best.
        </Typography>
        <View className="flex flex-row gap-2">
          {homeBeautyServiceTypeOptions.map((option) => {
            return (
              <Card
                key={option.title}
                className="flex-1 cursor-pointer p-2"
                onClick={() =>
                  setBookingState({
                    packageDetailsFilter: {
                      ...packageDetailsFilter,
                      serviceTypes: [option.value],
                    },
                  })
                }
                variant={service === option.value ? "info" : "default"}
              >
                <Typography variant="body-md">
                  {getPlatformTitle(option.title)}
                </Typography>
                <Typography
                  variant="caption-sm"
                  color="foreground-intermediate"
                >
                  Starting from
                </Typography>
                <View className="flex flex-row items-center">
                  <Typography variant="label-sm" color="brand-primary">
                    {formatPrice(option.price)}
                  </Typography>
                  <Typography
                    variant="caption-md"
                    color="foreground-intermediate"
                  >
                    /session
                  </Typography>
                </View>
              </Card>
            );
          })}
        </View>

        <Card className="bg-background-intermediate p-4">
          {choosePackageLoading && <Spin className="my-12 size-12 shrink-0" />}
          <RadioGroup value={selectedPackageIds[0]} onValueChange={() => {}}>
            {options.map((option) => {
              return (
                <ServiceRadioCard
                  key={option.value}
                  active={selectedPackageIds[0] === option.value}
                  onSelect={() => onSelect(option.value)}
                  imgUrl={option.imgUrl}
                  title={option.title}
                  subTitle={option.subTitle}
                  thirdTitle={option.thirdTitle}
                  value={option.value}
                />
              );
            })}
          </RadioGroup>
        </Card>

        <Button
          variant="primary"
          color="CTA"
          size="lg"
          rounded="full"
          onClick={onContinue}
          children="Continue"
        />
      </View>
    </BottomDrawerModal>
  );
};
